import { useCallback, useEffect, useRef, useState } from "react";

import { alCambiarPlanilla, traerPlanilla } from "../services/espacio";
import { esPropio, quienGuardo } from "../shared/espacioAcceso";

/* FiboSheets #6: la planilla abierta se actualiza sola cuando otro la guarda.
 *
 * Aparte de MiEspacio.jsx a propósito: ese archivo lo tocan varios a la vez,
 * y esto es una pieza con su propia regla. El editor le presta su `guardado`
 * (el ref del guardado optimista: versión, base, cola, frenado…) y una
 * función para poner una planilla recién leída sin moverse de lugar.
 *
 * LA REGLA, cuando llega por realtime que la planilla cambió:
 *   · Si es el eco de un guardado de ESTA pestaña → nada.
 *   · Si acá NO hay nada pendiente → se relee y se pone en silencio, en la
 *     misma hoja, con la misma celda elegida y el mismo scroll (no se
 *     remonta nada). Y un cartelito chico dice quién la cambió.
 *   · Si acá HAY algo pendiente (escribiendo, viajando o ya en conflicto) →
 *     NO se pisa: se avisa «X cambió la planilla» y se deja que el guardado
 *     optimista choque como siempre (con «Recargar» / «Guardar mi versión»).
 * Edición simultánea celda a celda está fuera de alcance: el que llega
 * segundo elige.
 *
 * Si el realtime no se puede armar (sin red, o un test que mockea el
 * servicio entero), la planilla anda igual que antes: el guardado optimista
 * sigue cuidando que nadie pise a nadie. */

export const ACTUALIZADA = "actualizada";
export const PENDIENTE = "pendiente";

/* ¿Hay algo de esta pestaña que todavía no llegó a la base? Esperando sus
   800 ms, viajando, o rebotado por un conflicto. */
const hayPendiente = (G) => !G.soltada && (G.version !== G.guardada || G.frenado || !!G.timer);

export function usePlanillaEnVivo({ id, activo, guardado, yo, alRecargar, alCambiarAcceso }) {
  const [aviso, setAviso] = useState(null);
  // Lo último que pasó el editor: el efecto no se rearma en cada render.
  const props = useRef({});
  props.current = { yo, alRecargar, alCambiarAcceso };

  useEffect(() => {
    if (!activo || !id) return undefined;
    let vivo = true;
    let cola = Promise.resolve();
    let juntadas = []; // los avisos que llegaron y todavía no se atendieron

    const manejar = async () => {
      // Los que llegaron juntos se atienden de una: el que escribe de
      // corrido guarda cada 800 ms, y releer por cada aviso sería traer la
      // planilla entera una y otra vez para nada.
      const filas = juntadas;
      juntadas = [];
      if (!filas.length || !vivo) return;
      const G = guardado.current;
      // Lo que esta pestaña tenga viajando termina primero: recién ahí se
      // sabe si lo que llegó es el eco de lo propio.
      await G.cola;
      if (!vivo || G.soltada) return;
      const ajenas = filas.filter((f) => f.actualizado_en && !esPropio(G, f.actualizado_en));
      if (!ajenas.length) return;
      const fila = ajenas[ajenas.length - 1];
      const { yo: mail } = props.current;
      if (hayPendiente(G)) {
        setAviso({ tipo: PENDIENTE, quien: quienGuardo(fila.actualizado_por, mail), lectura: G.lectura });
        return;
      }
      const version = G.version;
      const lectura = G.lectura;
      let p;
      try {
        p = await traerPlanilla(id);
      } catch {
        return; // No se pudo releer: el próximo guardado igual lo detecta.
      }
      if (!vivo || G.soltada || G.lectura !== lectura || !p) return;
      // Se escribió algo mientras viajaba la lectura: eso no se pisa.
      if (G.version !== version || hayPendiente(G)) {
        setAviso({ tipo: PENDIENTE, quien: quienGuardo(fila.actualizado_por, mail), lectura: G.lectura });
        return;
      }
      if (esPropio(G, p.actualizado_en)) return;
      props.current.alRecargar(p);
      setAviso({
        tipo: ACTUALIZADA,
        quien: quienGuardo(p.actualizado_por || fila.actualizado_por, mail),
        lectura: guardado.current.lectura,
      });
    };

    let soltar = () => {};
    try {
      soltar = alCambiarPlanilla(id, (fila) => {
        const f = fila || {};
        // El permiso (compartir, editar) se aplica en el acto: no depende de
        // lo que haya pendiente.
        if (f.editores || f.compartida_con) props.current.alCambiarAcceso?.(f);
        juntadas.push(f);
        cola = cola.then(manejar).catch(() => {});
      }) || soltar;
    } catch {
      // Sin realtime: como antes de esto.
    }
    return () => {
      vivo = false;
      try { soltar(); } catch { /* ya estaba cerrado */ }
    };
  }, [id, activo, guardado]);

  const cerrarAviso = useCallback(() => setAviso(null), []);
  // Un aviso vale para la lectura en la que se dio: «Recargar» lo vuelve viejo.
  const vigente = aviso && aviso.lectura === guardado.current.lectura ? aviso : null;
  return { aviso: vigente, cerrarAviso };
}

/* El cartel. El de PENDIENTE es un aviso de verdad (lo tuyo no se guardó
   todavía); el de ACTUALIZADA, una nota al pasar.
   El de PENDIENTE va en la franja fija de arriba del editor (`.esp-flotantes`,
   ver MiEspacio.jsx): con `panel-aviso` a secas era la tapa del hub (absoluta,
   z-index 1) y la fila de las letras de la grilla le pasaba por encima. */
export function AvisoEnVivo({ aviso, onCerrar }) {
  if (!aviso) return null;
  if (aviso.tipo === PENDIENTE) {
    return (
      <p className="esp-flotante mal" role="alert">
        <span>
          <b>{aviso.quien}</b> cambió la planilla recién. Lo que escribiste vos
          todavía no se guardó y <b>no lo pisamos</b>: al guardar vas a poder
          elegir entre recargar lo nuevo o guardar tu versión.
        </span>
        <button className="esp-btn chico" onClick={onCerrar} aria-label="Cerrar el aviso">Entendido</button>
      </p>
    );
  }
  return (
    <p className="panel-aviso suave esp-aviso" role="status">
      <span>Se actualizó sola con lo que guardó <b>{aviso.quien}</b>.</span>
      <button className="esp-btn chico" onClick={onCerrar} aria-label="Cerrar el aviso">×</button>
    </p>
  );
}

/* El link directo a una planilla que no puedo abrir. La base no dice por
   qué (cero filas es cero filas), así que se dicen las tres posibilidades. */
export function PlanillaSinAcceso({ onVolver }) {
  return (
    <div className="panel-aviso" role="alert">
      <h3>No podés abrir esta planilla</h3>
      {onVolver && <button className="esp-btn" onClick={onVolver}>← Mis planillas</button>}
    </div>
  );
}
